import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuth } from '../../hooks/useAuth';
import { LoginModal } from '../../components/feature/LoginModal';
import { feistyPets } from '../../mocks/feistyPets';

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user, signInWithGoogle, signOut } = useAuth();
  const { userProfile } = useUserProfile();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string>('');

  const capturedCount = userProfile?.collection.captured.length || 0;
  const totalPets = feistyPets.length; // Total dinâmico de Feisty Pets
  const progress = capturedCount > 0 ? Math.round((capturedCount / totalPets) * 100) : 0;
  const achievementsCount = userProfile?.achievements?.filter(a => a.unlocked).length || 0;

  // Contadores dinâmicos de modelos, expressões e tamanhos únicos
  const uniqueModels = feistyPets.length; // Total de modelos = 17
  const uniqueExpressions = new Set(feistyPets.flatMap(pet => pet.expressions)).size;
  const uniqueSizes = new Set(feistyPets.map(pet => pet.size)).size;

  // Estados para animação dos números
  const [animatedCaptured, setAnimatedCaptured] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [animatedAchievements, setAnimatedAchievements] = useState(0);

  // Animação dos números ao carregar
  useEffect(() => {
    if (!userProfile) return;

    const duration = 2000; // 2 segundos
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progressRatio = currentStep / steps;

      setAnimatedCaptured(Math.round(capturedCount * progressRatio));
      setAnimatedProgress(Math.round(progress * progressRatio));
      setAnimatedAchievements(Math.round(achievementsCount * progressRatio));

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedCaptured(capturedCount);
        setAnimatedProgress(progress);
        setAnimatedAchievements(achievementsCount);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [userProfile, capturedCount, progress, achievementsCount]);

  const handleOpenDex = () => {
    if (!user) {
      setLoginError('');
      setShowLoginModal(true);
    } else {
      navigate('/lista');
    }
  };

  const handleLogin = async () => {
    setIsLoggingIn(true);
    setLoginError('');
    try {
      await signInWithGoogle();
      setShowLoginModal(false);
      navigate('/lista');
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      setLoginError(error.message || 'Erro ao fazer login. Por favor, tente novamente.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
    setLoginError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-cyan-50 pb-20">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/105c755370d4a1110dc98a28fdd9b24e.jpg" 
                alt="FeistyDéx Logo" 
                className="w-12 h-12 object-cover rounded-full border-2 border-red-400"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">FeistyDéx</h1>
                <p className="text-xs text-gray-600">Coleção de Feisty Pets</p>
              </div>
            </div>
            
            {/* Área do usuário */}
            {user && (
              <div className="flex items-center gap-3">
                <img 
                  src={user.photoURL || 'https://via.placeholder.com/40'} 
                  alt={user.displayName || 'Usuário'} 
                  className="w-10 h-10 rounded-full border-2 border-cyan-400"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-semibold text-gray-800">{user.displayName}</p>
                  <button 
                    onClick={signOut}
                    className="text-xs text-red-600 hover:text-red-700 cursor-pointer"
                  >
                    Sair
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Login */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLogin}
        loading={isLoggingIn}
        error={loginError}
      />

      {/* Conteúdo Central Alinhado */}
      <div className="flex flex-col items-center justify-center px-4 pt-12">
        {/* Título */}
        <h1 className="text-5xl font-black text-gray-800 mb-2 text-center bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 bg-clip-text text-transparent">
          Feisty Pets MasterDéx
        </h1>
        
        <p className="text-lg text-gray-600 mb-8 text-center max-w-md">
          Capture todos os Feisty Pets, desbloqueie conquistas e organize sua coleção.
        </p>

        {/* Botão Principal */}
        <button
          onClick={handleOpenDex}
          className="bg-gradient-to-r from-red-600 to-red-700 text-white px-10 py-5 rounded-full text-lg font-bold shadow-2xl hover:shadow-3xl hover:scale-110 transition-all mb-12 animate-pulse whitespace-nowrap cursor-pointer"
        >
          <i className="ri-book-open-line mr-3 text-2xl"></i>
          {user ? 'Abrir FeistyDéx' : 'Fazer Login para Abrir'}
        </button>

        {/* Estatísticas Animadas da Coleção */}
        {userProfile && (
          <div className="mb-8 w-full max-w-2xl px-2">
            <div className="bg-white rounded-2xl p-4 sm:p-6 border-2 border-gray-200 shadow-xl">
              <h3 className="text-gray-800 text-lg sm:text-xl font-bold mb-4">Sua Coleção</h3>
              
              {/* Grid de Estatísticas */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
                {/* Pets Capturados */}
                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-2 sm:p-4 border border-cyan-200 transform transition-all duration-300 hover:scale-105">
                  <div className="text-2xl sm:text-4xl font-bold text-cyan-600 mb-1 tabular-nums">
                    {animatedCaptured}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-700 leading-tight">Pets Capturados</div>
                  <div className="text-[9px] sm:text-xs text-gray-500 mt-1">de {totalPets}</div>
                </div>

                {/* Progresso */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-2 sm:p-4 border border-red-200 transform transition-all duration-300 hover:scale-105">
                  <div className="text-2xl sm:text-4xl font-bold text-red-600 mb-1 tabular-nums">
                    {animatedProgress}%
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-700 leading-tight">Progresso</div>
                  <div className="text-[9px] sm:text-xs text-gray-500 mt-1">da coleção</div>
                </div>

                {/* Conquistas */}
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-2 sm:p-4 border border-yellow-200 transform transition-all duration-300 hover:scale-105">
                  <div className="text-2xl sm:text-4xl font-bold text-yellow-600 mb-1 tabular-nums">
                    {animatedAchievements}
                  </div>
                  <div className="text-[10px] sm:text-xs text-gray-700 leading-tight">Conquistas</div>
                  <div className="text-[9px] sm:text-xs text-gray-500 mt-1">desbloqueadas</div>
                </div>
              </div>

              {/* Barra de Progresso Visual */}
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-700 font-semibold text-xs sm:text-sm">Progresso da Coleção</span>
                <span className="text-gray-800 font-bold text-base sm:text-lg">{animatedProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 h-full rounded-full transition-all duration-[2000ms] ease-out"
                  style={{ width: `${animatedProgress}%` }}
                />
              </div>
              <div className="text-gray-500 text-xs mt-2">
                {progress === 100 ? '🎉 Coleção Completa!' : 'Continue capturando!'}
              </div>
            </div>
          </div>
        )}

        {/* Estatísticas do Jogo */}
        <div className="mt-8 w-full max-w-2xl px-2">
          <div className="bg-white rounded-2xl p-4 sm:p-6 border-2 border-gray-200 shadow-xl">
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              {/* Modelos */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-4 border border-gray-200 transform transition-all duration-300 hover:scale-105">
                <div className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">{uniqueModels}</div>
                <div className="text-xs sm:text-sm text-gray-600">Modelos</div>
              </div>

              {/* Expressões */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-4 border border-gray-200 transform transition-all duration-300 hover:scale-105">
                <div className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">{uniqueExpressions}</div>
                <div className="text-xs sm:text-sm text-gray-600">Expressões</div>
              </div>

              {/* Tamanhos */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-4 border border-gray-200 transform transition-all duration-300 hover:scale-105">
                <div className="text-2xl sm:text-3xl font-bold text-gray-800 mb-1">{uniqueSizes}</div>
                <div className="text-xs sm:text-sm text-gray-600">Tamanhos</div>
              </div>
            </div>
          </div>
        </div>

        {/* Assinatura */}
        <div className="mt-12 text-gray-500 text-sm">
          Feisty Pets MasterDéx by <strong className="text-gray-700">Ovelha</strong>
        </div>
      </div>
    </div>
  );
}
